import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";
import {logOperationalMetric} from "../../util/CommonUtils";

const WIDGET_PROCESS_NAME = "widgetService";
const OCULUS_PROCESS_NAME = "oculus";
// Operational metrics follow the same convention as the other API helpers in this
// package (GetCPTList, GetDrillDownResults, ...): a latency+count metric on success
// and a "<metricName>Error" count on failure, so widget API regressions surface in
// the CSM dashboards/alarms instead of only in the browser console.
const METRIC_PROCESS_NAME = "SCC_RTS_WIDGET";
const GET_WIDGETS_METRIC = "getRTSWidgets";
const GET_METRIC_DATA_METRIC = "getRTSMetricData";

export const RTS_WIDGET_IDS = [
    "RTS_STOW_COMPLIANCE",
    "RTS_INDUCT_COMPLIANCE",
    "PENDING_REVERSE_STOW",
    "PENDING_REVERSE_INDUCT"
];

function buildMetricResultMap(responseData) {
    if (!responseData || !Array.isArray(responseData.metricResults)) {
        return {};
    }

    return responseData.metricResults.reduce((resultMap, metricResult) => {
        if (metricResult && metricResult.metric && metricResult.metric.metricName) {
            resultMap[metricResult.metric.metricName] = metricResult;
        }
        return resultMap;
    }, {});
}

/**
 * Fetches widget definitions from WidgetConfigStoreService.
 * Returns the widget config (display name, type, thresholds, data source).
 */
export async function getRTSWidgets() {
    const request = {
        resourcePath: "widget/GetWidgets",
        httpMethod: "post",
        processName: WIDGET_PROCESS_NAME,
        requestBody: { widgetIds: RTS_WIDGET_IDS }
    };
    const startTime = Date.now();
    let response;
    try {
        response = await getAPIGatewayDataFromParent(request);
    } catch (err) {
        logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_WIDGETS_METRIC, false);
        throw err;
    }
    if (response.status !== 200) {
        logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_WIDGETS_METRIC, false);
        throw new Error(`GetWidgets failed with status ${response.status}`);
    }
    logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_WIDGETS_METRIC, true);
    return response.data;
}

function buildRTSMetricDimensions(stationCode, filters = {}) {
    const dimensions = [{ name: "STATION_CODE", value: stationCode }];

    if (filters.startPDD) {
        dimensions.push({ name: "EAD_START", value: filters.startPDD });
    }
    if (filters.endPDD) {
        dimensions.push({ name: "EAD_END", value: filters.endPDD });
    }
    (filters.packageTypes_Reverse || []).forEach(packageType => {
        dimensions.push({ name: "SHIPMENT_TYPE", value: packageType });
    });

    return dimensions;
}

/**
 * Fetches metric data for a single RTS widget from OculusService GetMetricData.
 * @param {string} metricName - One of RTS_STOW_COMPLIANCE, RTS_INDUCT_COMPLIANCE, PENDING_REVERSE_STOW, PENDING_REVERSE_INDUCT
 * @param {string} stationCode - The station code (nodeId)
 * @param {object} filters - RTS filters to forward as metric dimensions
 */
export async function getRTSMetricData(metricName, stationCode, filters = {}) {
    const request = {
        resourcePath: "/os/getMetricData",
        httpMethod: "post",
        processName: OCULUS_PROCESS_NAME,
        requestBody: {
            metricDataQueries: [{
                metric: {
                    metricName: metricName,
                    dimensions: buildRTSMetricDimensions(stationCode, filters)
                }
            }]
        }
    };
    const startTime = Date.now();
    let response;
    try {
        response = await getAPIGatewayDataFromParent(request);
    } catch (err) {
        logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_METRIC_DATA_METRIC, false);
        throw err;
    }
    if (response.status !== 200) {
        logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_METRIC_DATA_METRIC, false);
        throw new Error(`GetMetricData failed for ${metricName} with status ${response.status}`);
    }
    logOperationalMetric(startTime, METRIC_PROCESS_NAME, GET_METRIC_DATA_METRIC, true);
    return buildMetricResultMap(response.data)[metricName] || null;
}

/**
 * Fetches all 4 RTS metric values in parallel.
 * @param {string} stationCode - The station code
 * @param {object} filters - RTS filters to forward as metric dimensions
 * @returns {Promise<Object>} Map of metricName -> metricResult
 */
export async function fetchAllRTSMetrics(stationCode, filters = {}) {
    if (!stationCode) {
        return {};
    }

    const metricResults = await Promise.all(RTS_WIDGET_IDS.map(async (metricName) => {
        try {
            return [metricName, await getRTSMetricData(metricName, stationCode, filters)];
        } catch (err) {
            console.warn(`Failed to fetch RTS metric ${metricName}:`, err);
            return [metricName, null];
        }
    }));

    return metricResults.reduce((results, [metricName, metricResult]) => {
        if (metricResult) {
            results[metricName] = metricResult;
        }
        return results;
    }, {});
}



// WEBPACK FOOTER //
// ./src/commons/api/RTSWidgetAPI.js